import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Scene as SceneType } from '../types/game';
import TypeWriter from './TypeWriter.tsx';

interface SceneProps {
  scene: SceneType;
  onChoice?: (choice: 'A' | 'B') => void;
  showFeedback: boolean;
  feedback: string;
  isTransitioning: boolean;
  onLastSceneComplete?: () => void;
}

const Scene: React.FC<SceneProps> = ({ 
  scene, 
  onChoice, 
  showFeedback, 
  feedback, 
  isTransitioning,
  onLastSceneComplete 
}) => {
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  const [isFeedbackTypingComplete, setIsFeedbackTypingComplete] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.75 }}
      className="frelative w-full min-h-screen flex flex-col items-center"
    >
      <motion.div
        className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${scene.image})` }}
        initial={{ opacity: 0 }}
        animate={{ opacity: isTransitioning ? 0 : 1 }}
        transition={{ duration: 0.75 }}
      >
        <motion.div 
          className="absolute inset-0 bg-black"
          initial={{ opacity: 0.5 }}
          animate={{ opacity: isTransitioning ? 1 : 0.5 }}
          transition={{ duration: 0.75 }}
        />
      </motion.div>
      
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isTransitioning ? 0 : 1 }}
        transition={{ duration: 0.75 }}
        className="relative z-10 max-w-3xl mx-auto px-8 py-8 flex flex-col items-center"
      >
        <div className="prose prose-lg text-white mb-8">
          <TypeWriter
            text={scene.content}
            speed={25}
            onComplete={() => {
              setIsTypingComplete(true);
              if (scene.id === 'intro') {
                return;
              }
              if (!scene.choices && onLastSceneComplete) {
                onLastSceneComplete();
              }
            }}
          />
        </div>
        
        {isTypingComplete && scene.id === 'intro' && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: isTransitioning ? 0 : 1, y: 0 }}
            transition={{ duration: 0.75 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onChoice?.('A')}
            className="mt-8 px-8 py-4 bg-green-600 text-white text-xl font-semibold rounded-lg shadow-lg hover:bg-green-700 transition-colors"
          >
            探索这个星球
          </motion.button>
        )}

        {isTypingComplete && scene.id !== 'intro' && scene.choices && !showFeedback && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isTransitioning ? 0 : 1 }}
            transition={{ duration: 0.75 }}
            className="space-y-4 mt-8 w-full max-w-xl"
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onChoice?.('A')}
              className="w-full p-4 bg-green-600 bg-opacity-80 text-white rounded-lg shadow-md hover:bg-opacity-90 transition-all"
            >
              {scene.choices.A.text}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onChoice?.('B')}
              className="w-full p-4 bg-green-600 bg-opacity-80 text-white rounded-lg shadow-md hover:bg-opacity-90 transition-all"
            >
              {scene.choices.B.text}
            </motion.button>
          </motion.div>
        )}

        {showFeedback && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: isTransitioning ? 0 : 1, y: 0 }}
            transition={{ duration: 0.75 }}
            className="mt-8 p-6 bg-black bg-opacity-50 backdrop-blur-sm rounded-lg text-white w-full max-w-xl mx-auto"
          >
            <TypeWriter
              text={feedback}
              speed={25}
              onComplete={() => {
                setIsFeedbackTypingComplete(true);
              }}
            />
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default Scene; 